// services/ugc/testimoniesApi.ts
import type { AxiosRequestConfig, AxiosProgressEvent } from "axios";
import baseApi from "../../lib/baseApi";
import uploadApi from "../../lib/uploadApi";
import type { TestimonyType } from "../../types/ugc/testimonyTypes";
import { UGC_ENDPOINTS } from "./endpoint/endpoints";

const ROOT = UGC_ENDPOINTS.testimonies.root;

type ProgressOpts = {
  onUploadProgress?: (pct: number) => void;
  signal?: AbortSignal;
};

const withProgress = (
  opts?: { onUploadProgress?: (pct: number) => void; signal?: AbortSignal }
): AxiosRequestConfig => ({
  signal: opts?.signal,
  onUploadProgress: (evt: AxiosProgressEvent) => {
    if (!opts?.onUploadProgress) return;

    const pct =
      typeof evt.progress === "number"
        ? Math.round(evt.progress * 100)
        : evt.total
        ? Math.round((evt.loaded / evt.total) * 100)
        : 0;

    opts.onUploadProgress(pct);
  },
});

export const testimoniesApi = {
  // ------------------------------------
  // Summary (owner-only, 1 per type)
  // ------------------------------------
  getSummary: async () => {
    const { data } = await baseApi.get(`${ROOT}/summary/`);
    return data;
  },

  // ------------------------------------
  // Retrieve by slug
  // ------------------------------------
  getDetailBySlug: async (slug: string) => {
    const { data } = await baseApi.get(`${ROOT}/${slug}/`);
    return data;
  },

  // ------------------------------------
  // Create (type is inside payload)
  // ------------------------------------
  create: async (
    type: TestimonyType,
    payload: FormData | Record<string, any>,
    opts?: ProgressOpts
  ) => {
    const isFormData =
      typeof FormData !== "undefined" && payload instanceof FormData;

    const client = isFormData ? uploadApi : baseApi;

    // 👈 enforce type here (slice-safe)
    if (isFormData) {
      payload.append("type", type);
    } else {
      payload = { ...payload, type };
    }

    const { data } = await client.post(
      `${ROOT}/`,
      payload,
      withProgress(opts)
    );

    return data as { id: number; slug: string };
  },

  // ------------------------------------
  // Update by slug
  // ------------------------------------
  updateBySlug: async (
    type: TestimonyType,
    slug: string,
    payload: FormData | Record<string, any>,
    opts?: ProgressOpts
  ) => {
    const isFormData =
      typeof FormData !== "undefined" && payload instanceof FormData;

    const client = isFormData ? uploadApi : baseApi;

    if (isFormData) {
      payload.append("type", type);
    } else {
      payload = { ...payload, type };
    }

    const { data } = await client.patch(
      `${ROOT}/${slug}/`,
      payload,
      withProgress(opts)
    );

    return data;
  },

  // ------------------------------------
  // Delete
  // ------------------------------------
  removeBySlug: async (slug: string) => {
    const { status } = await baseApi.delete(`${ROOT}/${slug}/`);
    return status === 204;
  },

  // ------------------------------------
  // Owner list
  // ------------------------------------
  getMine: async () => {
    const { data } = await baseApi.get(`${ROOT}/me/`);
    return data;
  },

  // ------------------------------------
  // Feed
  // ------------------------------------
  getFeed: async (cursor?: string) => {
    const { data } = await baseApi.get(`${ROOT}/feed/`, {
      params: cursor ? { cursor } : undefined,
    });
    return data;
  },

  // ------------------------------------
  // Explore
  // ------------------------------------
  explore: async (cursor?: string) => {
    const { data } = await baseApi.get(`${ROOT}/explore/`, {
      params: cursor ? { cursor } : undefined,
    });
    return data;
  },
};
